import { useEffect, useState } from 'react';
import { api, type Dashboard as Snapshot } from '../api.js';
import { faDec, faInt } from '../labels.js';

/**
 * Screen 1a — the page that answers "what did the assistant do today, and
 * where did it come up short?".
 *
 * Every figure is a Tehran day, decided by the server. Two are not: how many
 * people are waiting for an operator and how many are on the site right now.
 * Those are the present moment, and they are the ones worth interrupting for.
 */

const REFRESH_MS = 30_000;

/** A sync that has not run in this long is worth an amber bar, not a footnote. */
const STALE_DAYS = 2;

const GATE_FA: Record<string, string> = {
  medical_referral: 'نشانه‌ی پزشکی — پاسخ متوقف شد',
  prescription_request: 'داروی نسخه‌ای — رد شد',
  llm_failed: 'مدل جواب نداد — به اپراتور رفت',
  llm_not_configured: 'مدل پیکربندی نشده بود',
  explicit_request: 'خودش اپراتور خواست',
  order_problem: 'مشکل سفارش — به اپراتور رفت',
};

/** Gates that mean a guardrail fired, as opposed to a route being taken. */
const SAFETY_GATES = ['medical_referral', 'prescription_request'];

function Tile({
  label,
  value,
  suffix,
  note,
  tone,
}: {
  label: string;
  value: string;
  suffix?: string | undefined;
  note?: string | undefined;
  tone?: string | undefined;
}) {
  return (
    /*
     * On a phone the tiles are cards in a two-column grid; on desktop they are
     * one band divided by hairlines. Same markup — the difference is whether
     * the border goes round a tile or runs along one edge of it.
     */
    <div className="rounded-xl border border-ink/[.09] p-4 md:rounded-none md:border-0 md:border-e md:border-ink/[.08] md:px-7 md:py-6 md:last:border-e-0">
      <div className="text-xs text-ink/50">{label}</div>
      <div className="mt-2 text-2xl font-bold tracking-tight md:mt-2.5 md:text-3xl">
        {value}
        {suffix && (
          <span className="ms-2 text-xs font-normal text-ink/40 md:text-md">{suffix}</span>
        )}
      </div>
      <div className={`mt-1.5 text-2xs font-medium md:mt-2.5 md:text-xs ${tone ?? 'text-ink/40'}`}>
        {note ?? ' '}
      </div>
    </div>
  );
}

function Panel({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: React.ReactNode | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-4 md:p-6">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-md font-bold">{title}</h2>
        {aside}
      </div>
      {children}
    </div>
  );
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

export function Dashboard({
  inboxId,
  onGoto,
  onSynced,
}: {
  inboxId?: string | undefined;
  onGoto: (tab: 'conversations' | 'catalog') => void;
  onSynced?: (() => void) | undefined;
}) {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [at, setAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [hideSync, setHideSync] = useState(false);

  async function load() {
    try {
      setSnap(await api.dashboard(inboxId));
      setAt(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در خواندن آمار');
    }
  }

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxId]);

  if (error && !snap) {
    return <div className="p-10 text-center text-sm text-bad">{error}</div>;
  }
  if (!snap) {
    return <div className="p-10 text-center text-sm text-ink/40">در حال خواندن…</div>;
  }

  const now = new Date();
  const stale = daysSince(snap.inbox?.lastSyncedAt ?? null);
  const syncOverdue = !hideSync && (stale === null || stale >= STALE_DAYS);

  const { conversations: convo, operator, assistant, usage, catalog } = snap;

  const delta =
    convo.yesterday > 0
      ? Math.round(((convo.today - convo.yesterday) / convo.yesterday) * 100)
      : null;

  const handoffShare =
    convo.today > 0 ? Math.round((operator.handoffsToday / convo.today) * 100) : null;

  const safety = snap.gates.filter((g) => SAFETY_GATES.includes(g.gate));
  const safetyTotal =
    safety.reduce((sum, g) => sum + g.n, 0) + assistant.rewrittenClaims;

  const routed = snap.gates.filter(
    (g) => !SAFETY_GATES.includes(g.gate) && GATE_FA[g.gate],
  );

  const tokens = usage.promptTokens + usage.completionTokens;
  const busiest = [...snap.byHour].sort((a, b) => b.n - a.n)[0] ?? null;
  const peak = Math.max(1, ...snap.byHour.map((h) => h.n));
  const worstMiss = Math.max(1, ...snap.shortfalls.map((s) => s.n));

  async function runSync() {
    if (!snap?.inbox) return;
    setSyncing(true);
    try {
      await api.syncCatalog(snap.inbox.id);
      await load();
      onSynced?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'سینک انجام نشد');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-paper-app">
      <header className="flex items-end justify-between gap-4 border-b border-ink/[.08] bg-white px-4 pb-4 pt-4 md:px-8 md:pb-5 md:pt-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight md:text-2xl">امروز</h1>
          <div className="mt-1.5 text-sm text-ink/50 md:mt-2 md:text-base">
            {now.toLocaleDateString('fa-IR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
            {' · تا ساعت '}
            {now.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        {/* No date-range control: the endpoint answers for today, and a picker
            that only has one option is a promise the page cannot keep. */}
        <span className="hidden rounded-lg border border-ink/[.12] px-3.5 py-2 text-sm font-medium text-ink/60 md:inline-block">
          امروز
        </span>
      </header>

      {syncOverdue && (
        <div className="flex flex-wrap items-center gap-3 border-b border-warn-line bg-warn-panel px-4 py-3 md:gap-3.5 md:px-8 md:py-3.5">
          <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-warn text-sm font-bold text-white">
            !
          </span>
          <div className="text-base font-bold text-warn-deep">
            {stale === null
              ? 'کاتالوگ هنوز یک‌بار هم سینک نشده.'
              : `سینک کاتالوگ ${faInt(stale)} روز است انجام نشده.`}
          </div>
          <div className="text-base text-warn-mid">
            {/* Why this bar exists: WooCommerce disables a webhook after five
                consecutive failures and says nothing, so a dead catalog looks
                exactly like a catalog that did not change. */}
            {snap.inbox?.lastSyncedAt
              ? `آخرین موفق ${new Date(snap.inbox.lastSyncedAt).toLocaleString('fa-IR', {
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit',
                })}`
              : 'تا سینک نشود، پیشنهادها از داده‌ی خالی می‌آیند.'}
          </div>
          <div className="flex w-full shrink-0 gap-2 md:ms-auto md:w-auto">
            <button
              className="btn-primary flex-1 px-3.5 py-2 text-sm md:flex-none"
              disabled={syncing}
              onClick={() => void runSync()}
            >
              {syncing ? 'در حال سینک…' : 'همین حالا سینک کن'}
            </button>
            <button
              className="btn px-3 py-2 text-sm font-medium text-warn-deep hover:bg-warn-line/40"
              onClick={() => setHideSync(true)}
            >
              بعداً
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2.5 bg-white p-4 md:grid-cols-5 md:gap-0 md:border-b md:border-ink/[.08] md:p-0">
        <Tile
          label="گفتگو"
          value={faInt(convo.today)}
          note={
            delta === null
              ? 'دیروز گفتگویی نبود'
              : `${faInt(Math.abs(delta))}٪ ${delta >= 0 ? '+' : '−'} از دیروز`
          }
          tone={delta === null ? undefined : delta >= 0 ? 'text-ok' : 'text-bad'}
        />
        <Tile
          label="رسیده به اپراتور"
          value={faInt(operator.handoffsToday)}
          suffix={handoffShare === null ? undefined : `${faInt(handoffShare)}٪`}
          note={
            operator.waitingNow > 0
              ? `${faInt(operator.waitingNow)} بی‌جواب مانده`
              : 'کسی در صف نیست'
          }
          tone={operator.waitingNow > 0 ? 'text-bad' : 'text-ok'}
        />
        <Tile
          label="محصول پیشنهاد شد"
          value={faInt(assistant.productsOffered)}
          note={`${faInt(assistant.uniqueProducts)} محصول یکتا`}
        />
        <Tile
          label="گفت «نداریم»"
          value={faInt(assistant.admittedDefeat)}
          note={
            assistant.replies > 0
              ? `از ${faInt(assistant.replies)} پاسخ`
              : 'پاسخی نداشت'
          }
          tone={assistant.admittedDefeat > 0 ? 'text-warn-text' : undefined}
        />
        <Tile
          label="میانگین پاسخ ربات"
          value={assistant.avgMs === null ? '—' : faDec(assistant.avgMs / 1000)}
          suffix={assistant.avgMs === null ? undefined : 'ثانیه'}
          note={
            assistant.slowestMs === null
              ? 'هنوز زمانی ثبت نشده'
              : `کندترین ${faDec(assistant.slowestMs / 1000)}`
          }
        />
      </div>

      <div className="grid flex-1 grid-cols-1 gap-4 p-4 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] md:p-5">
        <div className="card flex flex-col p-4 md:p-6">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-md font-bold">کجا کم آورد</h2>
            <div className="text-xs text-ink/40">
              {faInt(assistant.admittedDefeat)} بارِ «نداریم» + {faInt(safetyTotal)} توقف
              ایمنی
            </div>
          </div>

          <div className="mt-4">
            {snap.shortfalls.length === 0 && (
              <p className="py-8 text-center text-sm text-ink/40">
                امروز جایی کم نیاورد.
              </p>
            )}
            {snap.shortfalls.map((s, i) => (
              <div
                key={`${s.text}-${i}`}
                className="flex items-center gap-4 border-b border-ink/[.07] py-3.5 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-base font-medium leading-normal">
                    {s.text}
                  </div>
                  <div className="mt-1 text-xs text-ink/45">
                    {faInt(s.n)} بار پرسیده شد
                    {s.gate && SAFETY_GATES.includes(s.gate)
                      ? ' · گیت ایمنی پاسخ را گرفت'
                      : ' · چیزی برای پیشنهاد پیدا نشد'}
                  </div>
                </div>
                <div className="h-1.5 w-[110px] shrink-0 rounded-sm bg-ink/[.07]">
                  <div
                    className={`h-1.5 rounded-sm ${
                      s.gate && SAFETY_GATES.includes(s.gate) ? 'bg-bad' : 'bg-warn'
                    }`}
                    style={{ width: `${Math.round((s.n / worstMiss) * 100)}%` }}
                  />
                </div>
                <div className="w-6 text-start text-base font-bold">{faInt(s.n)}</div>
              </div>
            ))}
            {snap.shortfalls.length > 0 && (
              /* Said out loud, because the design's version implies clustering
                 and this list does not do that. Two wordings of one question
                 are two rows here. */
              <p className="mt-3 text-xs leading-relaxed text-ink/35">
                سؤال‌ها عیناً گروه می‌شوند — دو جور پرسیدنِ یک چیز، دو سطر است.
              </p>
            )}
          </div>

          <div className="mt-7 flex items-baseline justify-between">
            <h2 className="text-md font-bold">گفتگو در ساعت</h2>
            <div className="text-xs text-ink/40">
              {busiest
                ? `شلوغ‌ترین: ساعت ${faInt(busiest.hour)}`
                : 'هنوز گفتگویی نبوده'}
            </div>
          </div>
          <div className="-mx-1 overflow-x-auto px-1">
          <div className="mt-4 flex h-24 min-w-[420px] items-end gap-[5px]">
            {Array.from({ length: 24 }, (_, hour) => {
              const n = snap.byHour.find((h) => h.hour === hour)?.n ?? 0;
              const share = n / peak;
              return (
                <div
                  key={hour}
                  title={`ساعت ${hour}: ${n} گفتگو`}
                  className={`flex-1 rounded-t-sm ${
                    share > 0.75
                      ? 'bg-brand'
                      : share > 0.5
                        ? 'bg-brand-400'
                        : share > 0.25
                          ? 'bg-brand-300'
                          : 'bg-brand-200'
                  }`}
                  style={{ height: `${Math.max(2, share * 100)}%` }}
                />
              );
            })}
          </div>
          <div className="mt-2 flex min-w-[420px] justify-between text-2xs text-ink/35">
            <span>۰</span>
            <span>۶</span>
            <span>۱۲</span>
            <span>۱۸</span>
            <span>۲۳</span>
          </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <Panel
            title="گیت‌های ایمنی"
            aside={
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${
                  safetyTotal > 0 ? 'bg-bad-bg text-bad' : 'bg-paper-mute text-ink/45'
                }`}
              >
                {faInt(safetyTotal)} مورد
              </span>
            }
          >
            <div className="mt-3.5 flex flex-col gap-3">
              {safety.map((g) => (
                <div key={g.gate} className="flex items-baseline justify-between gap-3">
                  <span className="text-sm leading-normal text-ink/60">
                    {GATE_FA[g.gate] ?? g.gate}
                  </span>
                  <span className="text-base font-bold">{faInt(g.n)}</span>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm leading-normal text-ink/60">
                  ادعای درمانی — بازنویسی شد
                </span>
                <span className="text-base font-bold">
                  {faInt(assistant.rewrittenClaims)}
                </span>
              </div>
              {safetyTotal === 0 && (
                <p className="text-xs text-ink/40">امروز هیچ گاردریلی فعال نشد.</p>
              )}
            </div>

            {routed.length > 0 && (
              <div className="mt-4 border-t border-ink/[.07] pt-3.5">
                <div className="field-label mb-2.5">به اپراتور رفت</div>
                <div className="flex flex-col gap-2.5">
                  {routed.map((g) => (
                    <div key={g.gate} className="flex items-baseline justify-between gap-3">
                      <span className="text-sm text-ink/60">{GATE_FA[g.gate]}</span>
                      <span className="text-sm font-bold">{faInt(g.n)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          <Panel title="مصرف مدل">
            <div className="mt-3 flex items-baseline gap-2">
              <div className="text-xl font-bold tracking-tight">{faInt(tokens)}</div>
              <div className="text-sm text-ink/45">توکن امروز</div>
            </div>
            {tokens > 0 ? (
              <>
                <div className="mt-3.5 flex h-[7px] gap-0.5 overflow-hidden rounded-sm">
                  <div
                    className="rounded-sm bg-brand"
                    style={{ width: `${(usage.promptTokens / tokens) * 100}%` }}
                  />
                  <div
                    className="rounded-sm bg-warn"
                    style={{ width: `${(usage.completionTokens / tokens) * 100}%` }}
                  />
                </div>
                <div className="mt-2.5 flex justify-between text-xs text-ink/50">
                  <span>ورودی {faInt(usage.promptTokens)}</span>
                  <span>خروجی {faInt(usage.completionTokens)}</span>
                </div>
              </>
            ) : (
              <p className="mt-3 text-xs text-ink/40">امروز تماسی با مدل نبوده.</p>
            )}
          </Panel>

          <Panel title="صف بازبینی کاتالوگ">
            <div className="mt-3.5 flex flex-col gap-3">
              {[
                ['در انتظار بازبینی', catalog.pending],
                ['بدون دسته', catalog.uncategorized],
                ['رتبه‌ی دستی خورده', catalog.ranked],
              ].map(([label, n]) => (
                <div key={String(label)} className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-ink/60">{label}</span>
                  <span className="text-base font-bold">{faInt(Number(n))}</span>
                </div>
              ))}
            </div>
            <button
              className="btn-primary mt-4 px-4 py-2.5 text-sm"
              onClick={() => onGoto('catalog')}
            >
              شروع بازبینی
            </button>
          </Panel>

          <div className="mt-auto text-xs leading-relaxed text-ink/40">
            اعداد هر {faInt(REFRESH_MS / 1000)} ثانیه تازه می‌شوند
            {at &&
              ` · آخرین به‌روزرسانی ${at.toLocaleTimeString('fa-IR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}`}
            {error && <span className="text-bad"> · {error}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
